// AIFF and AIFF-C parser (Apple's Audio Interchange File Format).
//
// AIFF is an IFF file: a "FORM" chunk that holds typed chunks. Every number is
// big-endian, because IFF comes from Electronic Arts on the Amiga and Apple
// built AIFF for the Macintosh, both Motorola 68000 machines.
//
// As in the WAV parser, the 12-byte FORM header is its own 'header' segment and
// every chunk is top-level, so the byte map colours COMM, SSND and tags apart.

import { ascii, channelName, field, fmtBytes, fmtHz, fmtKbps, hex, matches, plural, u16be, u32be } from './bits';
import { parseId3v2 } from './id3';
import type { AudioInfo, Explanation, Field, FormatParser, Note, ParsedFile, PcmLayout, Picture, Segment, Tag } from './types';
import { chunkText, fitFields, fmtClock, midiName, padSegment, sampleFields, signedNum, timeBlocks, validId, type SampleFormat } from './wav';

const i16 = (b: Uint8Array, o: number) => (u16be(b, o) << 16) >> 16;
const i8 = (b: Uint8Array, o: number) => (b[o] << 24) >> 24;

/** AIFF-C compression types. `coding` is set when we can show sample values. */
const COMPRESSION: Record<string, { name: string; coding?: SampleFormat['coding']; le?: boolean; bytes?: number; lossless?: boolean; frames?: number }> = {
  NONE: { name: 'PCM, big-endian', coding: 'int', lossless: true },
  twos: { name: 'PCM, big-endian (twos)', coding: 'int', lossless: true },
  sowt: { name: 'PCM, little-endian (sowt)', coding: 'int', le: true, lossless: true },
  in24: { name: '24-bit PCM, big-endian', coding: 'int', bytes: 3, lossless: true },
  in32: { name: '32-bit PCM, big-endian', coding: 'int', bytes: 4, lossless: true },
  '23ni': { name: '24-bit PCM, little-endian', coding: 'int', bytes: 3, le: true, lossless: true },
  'raw ': { name: '8-bit PCM, unsigned', coding: 'uint8', bytes: 1, lossless: true },
  fl32: { name: '32-bit IEEE float', coding: 'float', bytes: 4, lossless: true },
  FL32: { name: '32-bit IEEE float', coding: 'float', bytes: 4, lossless: true },
  fl64: { name: '64-bit IEEE float', coding: 'float', bytes: 8, lossless: true },
  FL64: { name: '64-bit IEEE float', coding: 'float', bytes: 8, lossless: true },
  alaw: { name: 'A-law', coding: 'alaw', bytes: 1 },
  ALAW: { name: 'A-law', coding: 'alaw', bytes: 1 },
  ulaw: { name: 'µ-law', coding: 'ulaw', bytes: 1 },
  ULAW: { name: 'µ-law', coding: 'ulaw', bytes: 1 },
  ima4: { name: 'IMA 4:1 ADPCM', frames: 64 },
  MAC3: { name: 'MACE 3:1' },
  MAC6: { name: 'MACE 6:1' },
  GSM: { name: 'GSM 6.10', frames: 160 },
  'GSM ': { name: 'GSM 6.10', frames: 160 },
  Qclp: { name: 'Qualcomm PureVoice' },
  QDMC: { name: 'QDesign Music' },
  QDM2: { name: 'QDesign Music 2' },
};
const SHORT_CODEC: Record<string, string> = {
  alaw: 'A-law', ALAW: 'A-law', ulaw: 'µ-law', ULAW: 'µ-law', ima4: 'IMA ADPCM', MAC3: 'MACE', MAC6: 'MACE',
  GSM: 'GSM 6.10', 'GSM ': 'GSM 6.10', Qclp: 'QCELP', QDMC: 'QDesign', QDM2: 'QDesign',
};

/** Channel order from the AIFF spec. */
function aiffLabels(n: number): string[] {
  const t: Record<number, string[]> = { 1: ['M'], 2: ['L', 'R'], 3: ['L', 'R', 'C'], 4: ['FL', 'FR', 'BL', 'BR'], 6: ['L', 'Lc', 'C', 'R', 'Rc', 'S'] };
  return t[n] ?? Array.from({ length: n }, (_, i) => `Ch ${i + 1}`);
}

/** Decodes an 80-bit IEEE 754 extended float (big-endian), as used for the AIFF sample rate. */
export function readExtended(b: Uint8Array, o: number): number {
  const sign = b[o] >> 7;
  const exp = ((b[o] & 0x7f) << 8) | b[o + 1];
  const hi = u32be(b, o + 2), lo = u32be(b, o + 6);
  if (exp === 0 && hi === 0 && lo === 0) return 0;
  if (exp === 0x7fff) return hi || lo ? NaN : sign ? -Infinity : Infinity;
  const v = (hi * 4294967296 + lo) * 2 ** (exp - 16383 - 63);
  return sign ? -v : v;
}

/** Seconds since 1904-01-01 (the classic Mac OS epoch) as a date string. */
function macDate(sec: number): string {
  const d = new Date((sec - 2082844800) * 1000);
  return Number.isNaN(d.getTime()) ? '?' : d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

interface RawChunk {
  id: string;
  start: number;
  declared: number;
  bodyStart: number;
  bodyEnd: number;
  end: number;
  pad: boolean;
  clamped?: string;
  warning?: string;
}

interface Comm {
  channels: number;
  frames: number;
  sampleSize: number;
  rate: number;
  compression: string;
  compressionName?: string;
  fields: Field[];
}

interface Ctx {
  b: Uint8Array;
  size: number;
  aifc: boolean;
  comm?: Comm;
  tags: Tag[];
  pictures: Picture[];
  notes: Note[];
}

function walk(ctx: Ctx, formEnd: number): { chunks: RawChunk[]; tail: number } {
  const { b, size } = ctx;
  const chunks: RawChunk[] = [];
  let pos = 12;
  while (pos + 8 <= size && validId(b, pos)) {
    const id = ascii(b, pos, 4);
    const declared = u32be(b, pos + 4);
    let len = declared;
    const bodyStart = pos + 8;
    let warning: string | undefined;
    let clamped: string | undefined;
    if (pos >= formEnd && bodyStart + len > size) break;
    if (id === 'SSND' && (declared === 0 || declared === 0xffffffff) && bodyStart + 8 < size) {
      len = size - bodyStart;
      clamped = 'end of file';
      warning = `The size field says ${declared === 0 ? '0' : '0xFFFFFFFF'}, so the writer never filled it in. The audio is assumed to run to the end of the file.`;
    }
    if (bodyStart + len > size) {
      warning = `The chunk says it holds ${len.toLocaleString('en-US')} bytes, but the file ends after ${(size - bodyStart).toLocaleString('en-US')}. The file is probably truncated.`;
      len = size - bodyStart;
      clamped = 'end of file';
    }
    const bodyEnd = bodyStart + len;
    let pad = len % 2 === 1 && bodyEnd < size;
    if (pad && bodyEnd + 9 <= size && !validId(b, bodyEnd + 1) && validId(b, bodyEnd)) {
      pad = false;
      warning = 'This chunk has an odd size but no pad byte after it. The format requires one; some programs forget it.';
    }
    if (pos >= formEnd && !warning) warning = 'This chunk lies after the end of the FORM chunk, as given by the FORM size.';
    chunks.push({ id, start: pos, declared, bodyStart, bodyEnd, end: bodyEnd + (pad ? 1 : 0), pad, clamped, warning });
    pos = bodyEnd + (pad ? 1 : 0);
  }
  return { chunks, tail: pos };
}

function headerFields(c: RawChunk, extra: Field[] = []): Field[] {
  let sizeText = `${c.declared.toLocaleString('en-US')} bytes`;
  if (c.clamped) sizeText += ` · ${(c.bodyEnd - c.bodyStart).toLocaleString('en-US')} bytes actually present`;
  return fitFields([
    field('Chunk ID', `"${c.id}"`, c.start, 4, 'Four ASCII letters that name the chunk. Programs skip IDs they do not know.'),
    field('Chunk size', sizeText, c.start + 4, 4, 'Length of the chunk body, not counting these 8 bytes. Big-endian.', c.declared),
    ...extra,
  ], c.start, c.end);
}

function headerSeg(c: RawChunk, len = 8, extra: Field[] = []): Segment {
  const end = Math.min(c.bodyEnd, c.start + len);
  return { kind: 'header', type: 'aiff.chunk.header', name: 'Chunk header', start: c.start, end, fields: fitFields(headerFields(c, extra), c.start, end) };
}

function chunkSeg(
  ctx: Ctx, c: RawChunk, kind: Segment['kind'], type: string, body: { fields?: Field[]; children?: Segment[]; summary?: string; name?: string },
): Segment {
  const children: Segment[] = [headerSeg(c), ...(body.children ?? [])];
  if (c.pad) children.push(padSegment(ctx.b, c.bodyEnd, 'aiff.pad'));
  return {
    kind, type, name: body.name ?? `${c.id.trim() || c.id} chunk`, start: c.start, end: c.end, summary: body.summary,
    fields: fitFields([...headerFields(c), ...(body.fields ?? [])], c.start, c.end), children, warning: c.warning,
  };
}

function parseComm(ctx: Ctx, c: RawChunk): Comm {
  const { b } = ctx;
  const p = c.bodyStart;
  const channels = i16(b, p), frames = u32be(b, p + 2), sampleSize = i16(b, p + 6);
  const rate = readExtended(b, p + 8);
  const exp = ((b[p + 8] & 0x7f) << 8) | b[p + 9];
  const mant = (BigInt(u32be(b, p + 10)) << 32n) | BigInt(u32be(b, p + 14));
  const rateText = Number.isFinite(rate) ? `${rate.toLocaleString('en-US', { maximumFractionDigits: 4 })} Hz` : String(rate);
  const fields: Field[] = [
    field('Channels', `${channels} (${channelName(channels)})`, p, 2, 'Number of channels. Their samples are interleaved in each sample frame.', channels),
    field('Sample frames', frames.toLocaleString('en-US'), p + 2, 4, 'Total sample frames in the SSND chunk. Divide by the sample rate to get the length.', frames),
    field('Sample size', `${sampleSize} bits`, p + 6, 2, 'Bits per sample. Samples are stored in whole bytes, padded at the low end.', sampleSize),
    field('Sample rate', rateText, p + 8, 10, 'An 80-bit extended-precision float, the native number type of the 68881 maths chip in 1980s Macs.', rate),
    { name: 'Rate · sign', value: b[p + 8] >> 7 ? '1 (negative)' : '0 (positive)', raw: b[p + 8] >> 7, bit: (p + 8) * 8, bits: 1, note: 'The top bit is the sign.' },
    { name: 'Rate · exponent', value: `${exp} → 2^${exp - 16383}`, raw: exp, bit: (p + 8) * 8 + 1, bits: 15, note: 'The next 15 bits are the power of two, stored plus 16383.' },
    {
      name: 'Rate · mantissa', value: `0x${mant.toString(16).toUpperCase().padStart(16, '0')} → ${(Number(mant) / 2 ** 63).toFixed(6)} × 2^${exp - 16383} = ${rateText}`, raw: mant, bit: (p + 10) * 8, bits: 64,
      note: 'The last 64 bits are the digits, read as 1.xxx in binary. Value = mantissa × 2^exponent.',
    },
  ];
  const comm: Comm = { channels, frames, sampleSize, rate, compression: 'NONE', fields };
  if (ctx.aifc && c.bodyEnd - p >= 22) {
    const ct = ascii(b, p + 18, 4);
    comm.compression = ct;
    fields.push(field('Compression type', `"${ct}" · ${COMPRESSION[ct]?.name ?? 'unknown'}`, p + 18, 4, 'A four-letter code for how the samples are coded. "NONE" is plain big-endian PCM.'));
    if (c.bodyEnd - p >= 23) {
      const n = b[p + 22];
      const total = Math.min(c.bodyEnd - p - 22, 1 + n + ((1 + n) % 2));
      comm.compressionName = chunkText(b, p + 23, Math.min(n, total - 1));
      fields.push(field('Compression name', `"${comm.compressionName}"`, p + 22, total, 'A readable name for the coding, stored as a Pascal string (a length byte, then the text).'));
    }
  }
  return comm;
}

function sampleFormatOf(comm: Comm): SampleFormat | undefined {
  const info = COMPRESSION[comm.compression];
  if (!info?.coding || comm.channels <= 0) return undefined;
  const bytes = info.bytes ?? Math.ceil(comm.sampleSize / 8);
  if (bytes < 1 || bytes > 8 || (info.coding === 'int' && bytes > 4)) return undefined;
  return { coding: info.coding, bytes, bigEndian: !info.le, channels: comm.channels, blockAlign: bytes * comm.channels, labels: aiffLabels(comm.channels) };
}

function textChunk(ctx: Ctx, c: RawChunk, key: string, type: string, label: string): Segment {
  const len = c.bodyEnd - c.bodyStart;
  const text = chunkText(ctx.b, c.bodyStart, len);
  if (text) ctx.tags.push({ key, value: text, rawKey: c.id });
  return chunkSeg(ctx, c, 'metadata', type, {
    name: `${c.id.trim()} chunk (${label})`, summary: text ? `"${text.length > 80 ? text.slice(0, 80) + '…' : text}"` : '(empty)',
    fields: len > 0 ? [field(key, `"${text.length > 500 ? text.slice(0, 500) + '…' : text}"`, c.bodyStart, len, 'Plain text. Its length comes from the chunk size.')] : [],
  });
}

function buildComt(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const n = u16be(b, c.bodyStart);
  const kids: Segment[] = [];
  let p = c.bodyStart + 2;
  for (let i = 0; i < n && p + 8 <= c.bodyEnd; i++) {
    const ts = u32be(b, p), marker = i16(b, p + 4), cnt = Math.min(u16be(b, p + 6), c.bodyEnd - p - 8);
    const text = chunkText(b, p + 8, cnt);
    const end = Math.min(c.bodyEnd, p + 8 + cnt + (cnt % 2));
    const fields = [
      field('Time stamp', macDate(ts), p, 4, 'When the comment was written, in seconds since 1 January 1904 (the Mac epoch).', ts),
      field('Marker ID', marker || 'none', p + 4, 2, 'Links the comment to a marker. 0 means it is about the whole file.', marker),
      field('Length', cnt, p + 6, 2, 'Length of the text in bytes.'),
    ];
    if (cnt > 0) fields.push(field('Text', `"${text}"`, p + 8, cnt, 'The comment text.'));
    kids.push({ kind: 'metadata', type: 'aiff.comment', name: `Comment ${i + 1}`, start: p, end, summary: `"${text.slice(0, 80)}"`, fields });
    if (text) ctx.tags.push({ key: 'Comment', value: text, rawKey: 'COMT' });
    p = end;
  }
  return chunkSeg(ctx, c, 'metadata', 'aiff.chunk.COMT', {
    name: 'COMT chunk (comments)', fields: [field('Comment count', n, c.bodyStart, 2, 'How many comments follow.')], children: kids, summary: plural(kids.length, 'comment'),
  });
}

function buildMark(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const n = u16be(b, c.bodyStart);
  const sr = ctx.comm?.rate ?? 0;
  const kids: Segment[] = [];
  let p = c.bodyStart + 2;
  for (let i = 0; i < n && p + 7 <= c.bodyEnd; i++) {
    const id = i16(b, p), pos = u32be(b, p + 2), len = b[p + 6];
    const nameEnd = Math.min(c.bodyEnd, p + 7 + len + ((len + 1) % 2));
    const name = chunkText(b, p + 7, Math.min(len, c.bodyEnd - p - 7));
    const at = sr > 0 ? ` · ${fmtClock(Math.round((pos / sr) * 1000) / 1000)}` : '';
    kids.push({
      kind: 'index', type: 'aiff.marker', name: `Marker ${id}${name ? ` · ${name}` : ''}`, start: p, end: nameEnd, summary: `frame ${pos.toLocaleString('en-US')}${at}`,
      fields: [
        field('Marker ID', id, p, 2, 'A number that INST loops and comments use to refer to this marker.'),
        field('Position', `${pos.toLocaleString('en-US')}${at}`, p + 2, 4, 'The sample frame the marker sits before. 0 is the very start.', pos),
        field('Name', `"${name}"`, p + 6, nameEnd - p - 6, 'A Pascal string: a length byte, then the text, padded to an even length.'),
      ],
    });
    p = nameEnd;
  }
  return chunkSeg(ctx, c, 'index', 'aiff.chunk.MARK', {
    name: 'MARK chunk (markers)', fields: [field('Marker count', n, c.bodyStart, 2, 'How many markers follow.')], children: kids, summary: plural(kids.length, 'marker'),
  });
}

function buildInst(ctx: Ctx, c: RawChunk): Segment {
  const { b } = ctx;
  const p = c.bodyStart;
  const MODES = ['No loop', 'Forward', 'Forward/backward'];
  const loop = (name: string, o: number) =>
    field(name, `${MODES[i16(b, o)] ?? i16(b, o)} · markers ${i16(b, o + 2)}→${i16(b, o + 4)}`, o, 6, 'Play mode, then the begin and end marker IDs of the loop.');
  const fields = [
    field('Base note', `${b[p]} (${midiName(b[p])})`, p, 1, 'The MIDI note at which the sound plays at its recorded pitch.', b[p]),
    field('Detune', `${signedNum(i8(b, p + 1))} cents`, p + 1, 1, 'Pitch correction, −50 to +50 cents.', i8(b, p + 1)),
    field('Note range', `${midiName(b[p + 2])}–${midiName(b[p + 3])}`, p + 2, 2, 'The keys a sampler should use this sound for.'),
    field('Velocity range', `${b[p + 4]}–${b[p + 5]}`, p + 4, 2, 'How hard a key must be struck (1–127) to use this sound.'),
    field('Gain', `${signedNum(i16(b, p + 6))} dB`, p + 6, 2, 'Volume change to apply on playback.', i16(b, p + 6)),
    loop('Sustain loop', p + 8),
    loop('Release loop', p + 14),
  ];
  return chunkSeg(ctx, c, 'metadata', 'aiff.chunk.INST', { name: 'INST chunk (instrument)', fields, summary: `Base note ${midiName(b[p])}` });
}

function buildGeneric(ctx: Ctx, c: RawChunk, kind: Segment['kind'], type: string, note: string, summary?: string, name?: string): Segment {
  const len = c.bodyEnd - c.bodyStart;
  return chunkSeg(ctx, c, kind, type, { name, fields: len > 0 ? [field('Body', fmtBytes(len), c.bodyStart, len, note)] : [], summary: summary ?? fmtBytes(len) });
}

function parseAiff(b: Uint8Array): ParsedFile {
  const size = b.length;
  const formType = ascii(b, 8, 4);
  const aifc = formType === 'AIFC';
  const ctx: Ctx = { b, size, aifc, tags: [], pictures: [], notes: [] };
  const notes = ctx.notes;
  const formSize = u32be(b, 4);
  const formEnd = Math.min(size, formSize + 8);
  const hdrEnd = Math.min(12, size);
  const header: Segment = {
    kind: 'header', type: 'aiff.form', name: 'FORM header', start: 0, end: hdrEnd, summary: `FORM · ${formType}`,
    fields: fitFields([
      field('Chunk ID', '"FORM"', 0, 4, 'Marks an IFF file: a form made of chunks.'),
      field('FORM size', `${formSize.toLocaleString('en-US')} bytes`, 4, 4, 'Size of everything after these 8 bytes, so the file size minus 8. Big-endian.', formSize),
      field('Form type', `"${formType}"`, 8, 4, aifc ? 'AIFC is AIFF-C: AIFF with a compression type, added in 1991.' : 'AIFF: uncompressed big-endian audio.'),
    ], 0, hdrEnd),
  };
  if (formSize + 8 > size) header.warning = `The header says the file is ${(formSize + 8).toLocaleString('en-US')} bytes, but it is only ${size.toLocaleString('en-US')}. The file is probably truncated.`;
  else if (formSize + 8 < size) header.warning = `The header says the file is ${(formSize + 8).toLocaleString('en-US')} bytes, but it is ${size.toLocaleString('en-US')}. There are extra bytes after the FORM chunk.`;

  const { chunks, tail } = walk(ctx, formEnd);
  const commChunk = chunks.find((c) => c.id === 'COMM');
  if (commChunk) ctx.comm = parseComm(ctx, commChunk);
  const comm = ctx.comm;
  const sf = comm ? sampleFormatOf(comm) : undefined;
  const segments: Segment[] = [header];
  const frames: Segment[] = [];
  let audio: AudioInfo = { codec: 'Unknown', lossless: false };
  let pcm: PcmLayout | undefined;
  let audioBytes = 0;

  for (const c of chunks) {
    let seg: Segment;
    switch (c.id) {
      case 'COMM': {
        const info = COMPRESSION[comm!.compression];
        seg = chunkSeg(ctx, c, 'header', 'aiff.chunk.COMM', {
          name: 'COMM chunk (common)', fields: comm!.fields,
          summary: `${aifc ? `${comm!.compression.trim()} · ` : ''}${fmtHz(comm!.rate)} · ${channelName(comm!.channels)} · ${comm!.sampleSize}-bit${info ? '' : ' · unknown coding'}`,
        });
        break;
      }
      case 'SSND': {
        seg = chunkSeg(ctx, c, 'audio', 'aiff.chunk.SSND', { name: 'SSND chunk (sound data)' });
        if (frames.length === 0) {
          const res = buildSsnd(ctx, c, seg, sf);
          audio = res.audio;
          pcm = res.pcm;
          audioBytes = res.audioBytes;
          if (seg.time) frames.push(seg);
        } else seg.warning = 'A second SSND chunk. The format allows only one.';
        break;
      }
      case 'FVER': {
        const ts = u32be(b, c.bodyStart);
        seg = chunkSeg(ctx, c, 'header', 'aiff.chunk.FVER', {
          name: 'FVER chunk (format version)',
          fields: [field('Version time stamp', `${hex(ts, 8)} · ${ts === 0xa2805140 ? 'AIFF-C version 1' : 'unknown version'} (${macDate(ts)})`, c.bodyStart, 4, 'The version of the AIFF-C spec, given as the date it was written.', ts)],
          summary: ts === 0xa2805140 ? 'AIFF-C version 1' : hex(ts, 8),
        });
        break;
      }
      case 'NAME': seg = textChunk(ctx, c, 'Title', 'aiff.chunk.NAME', 'name'); break;
      case 'AUTH': seg = textChunk(ctx, c, 'Artist', 'aiff.chunk.AUTH', 'author'); break;
      case '(c) ': seg = textChunk(ctx, c, 'Copyright', 'aiff.chunk.copyright', 'copyright'); break;
      case 'ANNO': seg = textChunk(ctx, c, 'Comment', 'aiff.chunk.ANNO', 'annotation'); break;
      case 'COMT': seg = buildComt(ctx, c); break;
      case 'MARK': seg = buildMark(ctx, c); break;
      case 'INST': seg = buildInst(ctx, c); break;
      case 'APPL': {
        const sig = ascii(b, c.bodyStart, 4);
        const len = c.bodyEnd - c.bodyStart;
        seg = chunkSeg(ctx, c, 'metadata', 'aiff.chunk.APPL', {
          name: `APPL chunk (${sig})`, summary: `Private data for "${sig}"`,
          fields: [field('Application signature', `"${sig}"`, c.bodyStart, 4, 'The four-letter code of the program that owns this data.'),
            ...(len > 4 ? [field('Data', fmtBytes(len - 4), c.bodyStart + 4, len - 4, 'Private data only that program understands.')] : [])],
        });
        break;
      }
      case 'ID3 ':
      case 'id3 ': {
        const res = parseId3v2(b, c.bodyStart);
        const kids: Segment[] = [];
        if (res && res.segment.end <= c.bodyEnd) {
          kids.push(res.segment);
          ctx.tags.push(...res.tags);
          ctx.pictures.push(...res.pictures);
          notes.push(...res.notes);
        }
        const len = c.bodyEnd - c.bodyStart;
        seg = chunkSeg(ctx, c, 'metadata', 'aiff.chunk.id3', {
          name: `${c.id.trim()} chunk (ID3 tag)`, children: kids, summary: res ? res.segment.name : 'No valid ID3 header',
          fields: kids.length || len === 0 ? [] : [field('Data', fmtBytes(len), c.bodyStart, len, 'This does not start with a valid ID3v2 header.')],
        });
        break;
      }
      case 'CHAN': seg = buildGeneric(ctx, c, 'header', 'aiff.chunk.CHAN', 'An Apple CoreAudio channel layout.', undefined, 'CHAN chunk (channel layout)'); break;
      case 'MIDI': seg = buildGeneric(ctx, c, 'metadata', 'aiff.chunk.MIDI', 'Raw MIDI messages.', undefined, 'MIDI chunk'); break;
      case 'AESD': seg = buildGeneric(ctx, c, 'metadata', 'aiff.chunk.AESD', 'The 24 bytes of AES channel status data.', undefined, 'AESD chunk (AES status)'); break;
      default:
        seg = buildGeneric(ctx, c, 'unknown', 'aiff.chunk.unknown', `Contents of an unknown "${c.id}" chunk. Players skip it by its size.`, `Unknown chunk "${c.id}" · ${fmtBytes(c.bodyEnd - c.bodyStart)}`);
    }
    segments.push(seg);
  }
  if (tail < size) {
    segments.push({
      kind: 'unknown', type: 'aiff.trailing', name: 'Trailing bytes', start: tail, end: size, summary: fmtBytes(size - tail),
      warning: 'These bytes are not a valid chunk. They may be damage, or data another program added.',
    });
  }

  // ── Notes ──
  if (!comm) notes.push({ level: 'warn', text: 'There is no COMM chunk, so nothing says how to read the audio.' });
  if (!chunks.some((c) => c.id === 'SSND')) notes.push({ level: 'warn', text: 'There is no SSND chunk, so the file holds no audio.' });
  if (comm && sf) {
    const rate = comm.rate * sf.blockAlign * 8;
    notes.push({ level: 'info', text: `The data rate is ${fmtKbps(rate)}: every second stores ${Math.round(comm.rate).toLocaleString('en-US')} × ${plural(comm.channels, 'channel')} × ${sf.bytes * 8} bits.` });
    if (!sf.bigEndian) notes.push({ level: 'info', text: `The "${comm.compression}" coding stores samples little-endian ("sowt" is "twos" backwards). Apple added it so Intel Macs could skip the byte swap.` });
    else notes.push({ level: 'info', text: 'All numbers and samples are big-endian, the byte order of the Motorola 68000 CPUs in early Macs. WAV, from the Intel PC world, is little-endian.' });
    if (sf.bytes * 8 !== comm.sampleSize && sf.coding === 'int') notes.push({ level: 'info', text: `Each ${comm.sampleSize}-bit sample fills ${sf.bytes} bytes. The unused low bits are zero.` });
    const expected = comm.frames * sf.blockAlign;
    if (audioBytes < expected) notes.push({ level: 'warn', text: `COMM says there are ${comm.frames.toLocaleString('en-US')} sample frames, but the SSND chunk only holds ${Math.floor(audioBytes / sf.blockAlign).toLocaleString('en-US')}.` });
    else if (audioBytes > expected + sf.blockAlign) notes.push({ level: 'info', text: `The SSND chunk holds ${fmtBytes(audioBytes - expected)} more than the ${comm.frames.toLocaleString('en-US')} frames in COMM. Players ignore the extra.` });
  }
  if (comm) notes.push({ level: 'info', text: `The sample rate is an 80-bit float. It lets AIFF store rates like ${fmtHz(comm.rate)} exactly, and even fractional rates.` });
  if (audioBytes > 0) {
    const pct = (audioBytes / size) * 100;
    notes.push({ level: 'info', text: `The audio samples use ${pct > 99.99 ? 'over 99.99' : pct.toFixed(2)}% of the file. Headers and metadata take the other ${(size - audioBytes).toLocaleString('en-US')} bytes.` });
  }
  const odd = chunks.filter((c) => c.pad);
  if (odd.length) notes.push({ level: 'info', text: `${odd.map((c) => `"${c.id.trim()}"`).join(', ')} ${odd.length === 1 ? 'has' : 'have'} an odd size, so a pad byte follows. IFF chunks always start on an even offset.` });

  return { format: 'aiff', container: aifc ? 'AIFF-C' : 'AIFF', size, segments, frames, audio, tags: ctx.tags, pictures: ctx.pictures, notes, pcm };
}

function buildSsnd(ctx: Ctx, c: RawChunk, seg: Segment, sf: SampleFormat | undefined): { audio: AudioInfo; pcm?: PcmLayout; audioBytes: number } {
  const { b } = ctx;
  const comm = ctx.comm;
  const offset = u32be(b, c.bodyStart), blockSize = u32be(b, c.bodyStart + 4);
  const audioStart = Math.min(c.bodyEnd, c.bodyStart + 8 + offset);
  const audioEnd = c.bodyEnd;
  const audioBytes = audioEnd - audioStart;
  const extra = fitFields([
    field('Offset', `${offset} bytes`, c.bodyStart, 4, 'Bytes to skip before the first sample. Used to align audio to a disk block; almost always 0.', offset),
    field('Block size', `${blockSize} bytes`, c.bodyStart + 4, 4, 'The block size the offset aligns to. Almost always 0.', blockSize),
  ], c.start, c.end);
  if (offset > c.bodyEnd - c.bodyStart - 8) seg.warning = 'The offset points past the end of the chunk.';
  const hdr = headerSeg(c, 16, extra);
  hdr.name = 'Chunk header + offset';
  const fixed: Segment[] = [hdr];
  if (audioStart > c.bodyStart + 8) {
    fixed.push({ kind: 'padding', type: 'aiff.ssnd.offset', name: 'Alignment gap', start: c.bodyStart + 8, end: audioStart, summary: fmtBytes(audioStart - c.bodyStart - 8) });
  }
  const pad = c.pad ? [padSegment(b, c.bodyEnd, 'aiff.pad')] : [];
  const hdrFields = [...headerFields(c), ...extra];

  if (!comm) {
    seg.summary = `${fmtBytes(audioBytes)} · format unknown`;
    seg.children = [...fixed, ...pad];
    return { audio: { codec: 'Unknown', lossless: false }, audioBytes };
  }
  const info = COMPRESSION[comm.compression];
  const rate = comm.rate;
  const okRate = Number.isFinite(rate) && rate > 0;
  const described = okRate ? (comm.frames * (info?.frames ?? 1)) / rate : undefined;
  // Time for the bytes that are really present (differs from COMM when truncated).
  let present = described;
  if (sf && okRate) present = Math.floor(audioBytes / sf.blockAlign) / rate;
  const codec = sf?.coding === 'float' ? 'IEEE float' : sf?.coding === 'int' || sf?.coding === 'uint8' ? 'PCM' : SHORT_CODEC[comm.compression] ?? (info ? info.name : `"${comm.compression}"`);
  const audio: AudioInfo = {
    codec, sampleRate: okRate ? rate : undefined, channels: comm.channels || undefined, duration: described,
    totalSamples: comm.frames * (info?.frames ?? 1), lossless: !!info?.lossless,
    channelMode: comm.channels ? channelName(comm.channels) : undefined,
  };
  const endian = sf?.bigEndian === false ? 'little-endian' : 'big-endian';
  if (sf) {
    audio.bitsPerSample = sf.coding === 'alaw' || sf.coding === 'ulaw' ? 8 : sf.coding === 'float' ? sf.bytes * 8 : comm.sampleSize;
    audio.bitrate = Math.round(rate * sf.blockAlign * 8);
    audio.bitrateMode = audio.lossless ? 'Uncompressed' : 'CBR';
    audio.codecLong =
      sf.coding === 'uint8' ? 'Linear PCM, 8-bit unsigned integer'
      : sf.coding === 'int' ? `Linear PCM, ${comm.sampleSize}-bit signed integer, ${endian}`
      : sf.coding === 'float' ? `Linear PCM, ${sf.bytes * 8}-bit IEEE floating point, ${endian}`
      : `G.711 ${codec}, 8-bit companded`;
  } else {
    audio.codecLong = info?.name ?? `Unknown compression "${comm.compression}"`;
    if (described) audio.bitrate = Math.round((audioBytes * 8) / described);
  }
  if (present !== undefined && Number.isFinite(present)) seg.time = [0, present];
  seg.summary = `${fmtBytes(audioBytes)}${present !== undefined ? ` · ${fmtClock(Math.round(present * 100) / 100)}` : ''} of ${codec}`;
  seg.children = () => [
    ...fixed,
    ...timeBlocks(b, {
      start: audioStart, end: audioEnd, duration: present ?? 0, type: 'aiff.data.block',
      bytesPerSecond: sf && okRate ? rate * sf.blockAlign : undefined, align: sf ? sf.blockAlign : comm.compression === 'ima4' ? 34 * comm.channels : 1, sample: sf,
    }),
    ...pad,
  ];
  seg.fields = () => (sf
    ? [...hdrFields, ...sampleFields(b, audioStart, audioEnd, sf, 0, 16)]
    : fitFields([...hdrFields, field(`${codec} data`, fmtBytes(audioBytes), audioStart, audioBytes, 'Compressed audio. This tool does not decode it into sample values.')], c.start, c.end));

  let pcm: PcmLayout | undefined;
  if (sf && sf.coding !== 'alaw' && sf.coding !== 'ulaw' && okRate) {
    pcm = { dataStart: audioStart, dataEnd: audioEnd, channels: sf.channels, bitsPerSample: sf.bytes * 8, blockAlign: sf.blockAlign, float: sf.coding === 'float', bigEndian: sf.bigEndian, sampleRate: rate };
  }
  return { audio, pcm, audioBytes };
}

export const aiffParser: FormatParser = {
  id: 'aiff',
  detect(b) {
    return b.length >= 12 && matches(b, 0, 'FORM') && (matches(b, 8, 'AIFF') || matches(b, 8, 'AIFC'));
  },
  parse: parseAiff,
  explanations: {
    'aiff.form': {
      title: 'FORM header',
      body: 'The first 12 bytes. `FORM` marks an IFF file, the size covers the rest of the file, and `AIFF` or `AIFC` says it holds audio. Apple based AIFF on EA\'s IFF format in 1988. Both come from Motorola 68000 machines, so all numbers are **big-endian**.',
    },
    'aiff.chunk.header': {
      title: 'Chunk header',
      body: 'Every chunk starts with a four-letter ID and a 4-byte big-endian size. With the size, a program can jump over chunks it does not understand, so new chunk types do not break old players.',
    },
    'aiff.pad': {
      title: 'Pad byte',
      body: 'IFF chunks must start on an even byte offset. When a chunk has an odd size, one zero byte follows it. The chunk size does not count it.',
    },
    'aiff.chunk.COMM': {
      title: 'COMM chunk (common)',
      body: 'Says how to read the audio: channels, number of sample frames, bits per sample and sample rate. The rate is an unusual **80-bit float**, the native format of the maths chip in early Macs. AIFF-C adds a compression type, such as `NONE`, `sowt` (little-endian) or `fl32` (float).',
    },
    'aiff.chunk.SSND': {
      title: 'SSND chunk (sound data)',
      body: 'The audio itself. An offset and block size come first (almost always 0), then the samples, interleaved one sample frame at a time. In plain AIFF each sample is a big-endian signed integer.',
    },
    'aiff.ssnd.offset': {
      title: 'Alignment gap',
      body: 'Bytes the SSND offset tells players to skip. Old programs used it to start the audio on a disk block boundary for faster reads.',
    },
    'aiff.data.block': {
      title: 'Audio samples',
      body: 'A slice of the SSND chunk. It holds the samples for this span of time. Each sample is a number that gives the position of the speaker cone at one instant.',
    },
    'aiff.chunk.FVER': {
      title: 'FVER chunk (format version)',
      body: 'Required in AIFF-C files. It holds the version of the AIFF-C spec as a Mac time stamp. Only one version (May 1990) was ever used.',
    },
    'aiff.chunk.NAME': { title: 'NAME chunk', body: 'The name of the sound, as plain text. Players show it as the title.' },
    'aiff.chunk.AUTH': { title: 'AUTH chunk', body: 'The author of the sound, as plain text. Players show it as the artist.' },
    'aiff.chunk.copyright': { title: 'Copyright chunk', body: 'A copyright notice, as plain text. Its ID is `(c) ` with a space at the end.' },
    'aiff.chunk.ANNO': { title: 'ANNO chunk (annotation)', body: 'A free text comment. The spec suggests COMT for new files, but many programs still write ANNO.' },
    'aiff.chunk.COMT': {
      title: 'COMT chunk (comments)',
      body: 'A list of comments. Each has a time stamp and can link to a marker, so a comment can point at a moment in the sound.',
    },
    'aiff.comment': { title: 'Comment', body: 'One comment: a time stamp (seconds since 1904, the classic Mac epoch), an optional marker ID and the text.' },
    'aiff.chunk.MARK': {
      title: 'MARK chunk (markers)',
      body: 'A list of named positions in the audio. Loops in the INST chunk and comments in COMT point at markers by their ID.',
    },
    'aiff.marker': { title: 'Marker', body: 'One named position, given in sample frames. Divide it by the sample rate to get the time in seconds.' },
    'aiff.chunk.INST': {
      title: 'INST chunk (instrument)',
      body: 'Tells a sampler how to play the sound as an instrument: the root note, key and velocity range, and a sustain loop and release loop made from markers.',
    },
    'aiff.chunk.APPL': {
      title: 'APPL chunk (application)',
      body: 'Private data for one program, named by its four-letter signature. Other programs skip it.',
    },
    'aiff.chunk.id3': {
      title: 'ID3 chunk',
      body: 'An ID3v2 tag, the same kind MP3 files use, inside an IFF chunk. iTunes and other music software write it because it can hold more tags and cover art than NAME and AUTH.',
    },
    'aiff.chunk.CHAN': { title: 'CHAN chunk (channel layout)', body: 'An Apple CoreAudio channel layout. It says which speaker each channel is for.' },
    'aiff.chunk.MIDI': { title: 'MIDI chunk', body: 'MIDI data to go with the sound, for example to set up a synthesizer.' },
    'aiff.chunk.AESD': { title: 'AESD chunk (AES status)', body: 'The channel status bytes from an AES/EBU digital audio link, such as the emphasis setting.' },
    'aiff.chunk.unknown': {
      title: 'Unknown chunk',
      body: 'A chunk this tool does not know. The size in its header lets every player skip it safely.',
    },
    'aiff.trailing': {
      title: 'Trailing bytes',
      body: 'Bytes after the last complete chunk. They do not form a valid chunk header, so players ignore them.',
    },
  } satisfies Record<string, Explanation>,
};
